import { configureStore as starterKitConfigureStore, Middleware } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';
import thunk from 'redux-thunk';

import { formRecommendationMiddleware } from '@app/features/formRecommendation';
import loggerMiddleWare from '@app/libs/datadog/logger/middleware';
import { queryClient } from '@app/queryClient';
import { routerMiddleware } from '@app/store/history';

import * as actionTypes from './actionTypes';
import dataLayerMiddleware from './analytics/dataLayerMiddleware';
import analyticsMiddleware from './analytics/middleware';
import rootReducer from './rootReducer';
import rootSaga from './rootSaga';
import { PartialRootState, RootState } from './types';

/* eslint-disable global-require */

if (process.env.NODE_ENV !== 'production') {
  const types: { [index: string]: string } = actionTypes;
  const notMatchedActionTypes = Object.keys(types).filter((key) => types[key] !== key);

  if (notMatchedActionTypes.length !== 0) {
    throw new Error(
      `${notMatchedActionTypes.join(
        ', '
      )} 액션의 상수는 변수명과 문자열값이 일치하지 않습니다. 반드시 일치시켜주세요.`
    );
  }
}

const configureStore = (preloadedState: PartialRootState = {}) => {
  const sagaMiddleware = createSagaMiddleware({
    context: {
      queryClient,
    },
  });

  const middlewares: Array<Middleware> = [
    dataLayerMiddleware.middleware, // listener middleware는 항상 첫 번째에 위치해야 합니다. 참고: https://redux-toolkit.js.org/api/createListenerMiddleware#middleware
    routerMiddleware,
    thunk,
    sagaMiddleware,
  ];

  if (process.env.REACT_APP_TENANT_ENV === 'modusign') {
    middlewares.push(analyticsMiddleware, formRecommendationMiddleware);
  }

  /**
   * CREATE-REACT-APP에서 사용하는 react-script는 명령어에 따라
   * process.env.NODE_ENV를 강제합니다 'production' | 'development' | 'test'. @see https://create-react-app.dev/docs/adding-custom-environment-variables/
   * 스테이지/프로덕션 환경에서만 로거 미들웨어를 적용합니다.
   */
  if (process.env.NODE_ENV === 'production') {
    middlewares.push(loggerMiddleWare);
  }

  const store = starterKitConfigureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
        // File 객체를 redux에 저장할 때 lastModifiedDate 값이 항상 바뀌어 개발환경에서 redux-immutable-state-invariant 미들웨어에서 오류를 발생시킵니다.
        // 외부문서에서 File 객체를 redux에 저장하기 때문에 개발환경에서 오류가 발생하지 않도록 설정합니다.
        // 참고: https://github.com/BBB/dropzone-redux-form-example/issues/5
        immutableCheck: { ignoredPaths: ['externalDocument.upload.documents'] },
      }).concat(middlewares),
    preloadedState: preloadedState as RootState,
    devTools: {
      trace: process.env.REACT_APP_MODUSIGN_ENV !== 'production',
      traceLimit: 20,
    },
  });
  let rootSagaTask = sagaMiddleware.run(rootSaga);

  if (process.env.NODE_ENV !== 'production') {
    if (module.hot) {
      module.hot.accept('./rootReducer', () => {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const nextRootReducer = require('./rootReducer').default;
        store.replaceReducer(nextRootReducer);
      });

      // This implementation has some limitation (maintain running saga task flow, spwan task).
      // See: https://github.com/redux-saga/redux-saga/issues/22#issuecomment-218737951
      module.hot.accept('./rootSaga', () => {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const nextRootSaga = require('./rootSaga').default;
        rootSagaTask.cancel();
        rootSagaTask.toPromise().then(() => {
          rootSagaTask = sagaMiddleware.run(nextRootSaga);
        });
      });
    }
  }

  return store;
};

export default configureStore;
